import React, { useState } from "react";
import "./WallSelector.css";
import { useConfiguration } from "src/contexts/ConfigurationContext";

interface WandSelectorProps {
  onWallSelect: (wall: UiWall | null) => void;
  onWallDeselect: (wall: UiWall) => void;
  activeWalls: string[];
  storageWalls: string[];
}

export interface UiWall {
  id: string;
  type: string; // 'horizontal' or 'vertical'
  row: number;
  column: number;
  active: boolean;
  selected: boolean;
  inner: boolean;
  storage: boolean;
}

const getHorizontalWalls = (
  rows: number,
  columns: number,
  selectedWall: UiWall | null,
  activeWalls: string[],
  storageWalls: string[]
) => {
  // Horizontal walls generation
  const horizontalWalls: UiWall[] = [];

  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c < columns; c++) {
      const wallId = `h-${r}-${c}`;

      horizontalWalls.push({
        row: r,
        column: c,
        type: "horizontal",
        id: wallId,
        selected: selectedWall?.id === wallId,
        active: activeWalls.includes(wallId),
        inner: !(r === 0 || r === rows),
        storage: storageWalls.includes(wallId),
      });
    }
  }

  return horizontalWalls;
};

const getVerticalWalls = (
  rows: number,
  columns: number,
  selectedWall: UiWall | null,
  activeWalls: string[],
  storageWalls: string[]
) => {
  // Vertical walls generation
  const verticalWalls: UiWall[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c <= columns; c++) {
      const wallId = `v-${r}-${c}`;
      verticalWalls.push({
        row: r,
        column: c,
        type: "vertical",
        id: wallId,
        selected: selectedWall?.id === wallId,
        active: activeWalls.includes(wallId),
        inner: !(c === 0 || c === columns),
        storage: storageWalls.includes(wallId),
      });
    }
  }

  return verticalWalls;
};

const generateBeams = (rows: number, columns: number) => {
  const beamsArray: { row: number; column: number }[] = [];
  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c <= columns; c++) {
      beamsArray.push({ row: r, column: c });
    }
  }
  return beamsArray;
};

const WallSelector: React.FC<WandSelectorProps> = ({
  onWallSelect,
  onWallDeselect,
  activeWalls,
  storageWalls,
}) => {
  const { state, configurator } = useConfiguration();
  const rows = configurator.getRows();
  const columns = configurator.getColumns();

  const [selectedWall, setSelectedWall] = useState<UiWall | null>(null);
  const horizontalWalls = getHorizontalWalls(
    rows,
    columns,
    selectedWall,
    activeWalls,
    storageWalls
  );
  const verticalWalls = getVerticalWalls(
    rows,
    columns,
    selectedWall,
    activeWalls,
    storageWalls
  );
  const beams = generateBeams(rows, columns);

  const handleWallClick = (wall: UiWall | null) => {
    let isDeselect = false;

    if (
      !storageWalls.includes(wall?.id ?? "") &&
      wall &&
      wall.id === selectedWall?.id &&
      !selectedWall.selected &&
      wall.selected
    ) {
      isDeselect = true;
      onWallDeselect(wall);
    }

    setSelectedWall((prevSelectedWall) => {
      if (prevSelectedWall?.id === wall?.id) {
        return null;
      }

      return wall;
    });

    if (isDeselect) return;
    if (!storageWalls.includes(wall?.id ?? "")) onWallSelect(wall);
  };

  return (
    <div className="wand-selector">
      {Array.from({ length: rows * 2 + 1 }, (_, rowIndex) => {
        const rowElements = [];

        if (rowIndex % 2 === 0) {
          // Рисуем балки и горизонтальные стены
          for (let c = 0; c < columns; c++) {
            const beam = beams.find(
              (b) => b.row === rowIndex / 2 && b.column === c
            );
            const hWall =
              horizontalWalls.find(
                (hw) => hw.row === rowIndex / 2 && hw.column === c
              ) ?? null;

            rowElements.push(
              <div
                key={`beam-${beam?.row}-${beam?.column}`}
                className={`beam ${
                  rowIndex > 0 && c > 0 && c < columns ? "hidden" : ""
                }`}
              ></div>
            );

            rowElements.push(
              <div
                key={hWall?.id}
                className={`wall h-wall ${rowIndex > 0 ? "hidden" : ""} ${
                  hWall?.selected ? "selected" : ""
                } ${hWall?.active ? "active" : ""} ${
                  hWall?.storage ? "storage" : ""
                }`}
                onClick={() => handleWallClick(hWall)}
              ></div>
            );
          }

          // Add last beam in row
          const beam = beams.find(
            (b) => b.row === rowIndex / 2 && b.column === columns
          );
          rowElements.push(
            <div
              key={`beam-${beam?.row}-${beam?.column}`}
              className="beam"
            ></div>
          );
        } else {
          // Рисуем вертикальные стены и пространства
          for (let c = 0; c <= columns; c++) {
            const vWall = verticalWalls.find(
              (vw) => vw.row === (rowIndex - 1) / 2 && vw.column === c
            );

            if (vWall) {
              rowElements.push(
                <div
                  key={vWall.id}
                  className={`wall v-wall ${
                    state.roofType === "oase" && !state.spy.enabled
                      ? " hidden"
                      : c === 0 || c === columns
                      ? ""
                      : "hidden"
                  } ${vWall.selected ? "selected" : ""} ${
                    vWall?.active ? "active" : ""
                  } ${vWall.inner ? "inner" : ""} ${
                    vWall?.storage ? "storage" : ""
                  }`}
                  onClick={() => handleWallClick(vWall)}
                ></div>
              );
            }

            if (c < columns) {
              rowElements.push(
                <div
                  key={`space-${(rowIndex - 1) / 2}-${c}`}
                  className="space"
                ></div>
              );
            }
          }
        }

        return (
          <div key={`row-${rowIndex}`} className="row">
            {rowElements}
          </div>
        );
      })}
    </div>
  );
};

export default WallSelector;
